import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  listAllManagedUsers,
  createUser,
  deleteUser,
  resetUserPassword,
  addUserToGroup,
  removeUserFromGroup,
  sendWelcomeEmail,
  generateTempPassword,
} from '../services/graphService';
import { adminRequest, groupConfig } from '../msalConfig';
import './AdminPanel.css';

export const AdminPanel = () => {
  const { user: currentUser, getAccessToken } = useAuth();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [message, setMessage] = useState(null);
  const [filter, setFilter] = useState('');

  // Add user form
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('member');
  const [newPassword, setNewPassword] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  // Reset password dialog
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState('');

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const refreshUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const token = await getAccessToken(adminRequest.scopes);
      const managed = await listAllManagedUsers(token);
      setUsers(managed);
    } catch (err) {
      showMessage('Failed to load users: ' + err.message, 'error');
    } finally {
      setLoadingUsers(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    refreshUsers();
  }, [refreshUsers]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !newName.trim()) {
      showMessage('Email and display name are required', 'error');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      showMessage('Please enter a valid email address', 'error');
      return;
    }

    setAddingUser(true);
    const tempPw = newPassword || generateTempPassword();

    try {
      const token = await getAccessToken(adminRequest.scopes);

      // Create user in directory
      const created = await createUser(token, {
        email: newEmail.trim(),
        displayName: newName.trim(),
        password: tempPw,
      });

      // Add to appropriate group
      const groupId =
        newRole === 'admin'
          ? groupConfig.goaAdminGroupId
          : groupConfig.goaMembersGroupId;

      if (groupId) {
        await addUserToGroup(token, groupId, created.id);
      }

      // Always show credentials to admin (CIAM has no mailbox for /me/sendMail)
      setCreatedCredentials({
        email: newEmail.trim(),
        name: newName.trim(),
        password: tempPw,
      });

      // Attempt email if opted in (may fail in CIAM tenants)
      if (sendEmail) {
        try {
          await sendWelcomeEmail(token, {
            toEmail: newEmail.trim(),
            toName: newName.trim(),
            tempPassword: tempPw,
          });
          showMessage(`User ${newName.trim()} created and welcome email sent`);
        } catch (emailErr) {
          showMessage(
            `User created successfully. Email delivery unavailable in this tenant — share the credentials below manually.`,
            'warning'
          );
        }
      } else {
        showMessage(`User ${newName.trim()} created. Copy credentials below.`);
      }

      setNewEmail('');
      setNewName('');
      setNewPassword('');
      setNewRole('member');
      await refreshUsers();
    } catch (err) {
      showMessage('Failed to create user: ' + err.message, 'error');
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (userId === currentUser?.id) {
      showMessage('You cannot delete yourself', 'error');
      return;
    }
    if (!window.confirm(`Remove ${email}? This cannot be undone.`)) return;

    try {
      const token = await getAccessToken(adminRequest.scopes);
      await deleteUser(token, userId);
      showMessage(`Removed ${email}`);
      await refreshUsers();
    } catch (err) {
      showMessage('Failed to delete user: ' + err.message, 'error');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetTarget || !resetPw) return;

    try {
      const token = await getAccessToken(adminRequest.scopes);
      await resetUserPassword(token, resetTarget.id, resetPw);
      showMessage(`Password reset for ${resetTarget.displayName}. New password: ${resetPw}`);
      setResetTarget(null);
      setResetPw('');
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('Insufficient privileges') || msg.includes('Authorization_RequestDenied')) {
        showMessage(
          `Cannot reset password for ${resetTarget.displayName}. This user may use social login (Google/Facebook) and does not have a local password to reset.`,
          'error'
        );
      } else if (msg.includes('federated') || msg.includes('password does not exist')) {
        showMessage(
          `${resetTarget.displayName} uses a federated identity (social login). Password reset is not supported for this account type.`,
          'error'
        );
      } else {
        showMessage('Failed to reset password: ' + msg, 'error');
      }
    }
  };

  const handleChangeRole = async (userId, currentRole) => {
    if (userId === currentUser?.id) {
      showMessage('You cannot change your own role', 'error');
      return;
    }
    const targetRole = currentRole === 'admin' ? 'member' : 'admin';
    const fromGroup =
      currentRole === 'admin'
        ? groupConfig.goaAdminGroupId
        : groupConfig.goaMembersGroupId;
    const toGroup =
      targetRole === 'admin'
        ? groupConfig.goaAdminGroupId
        : groupConfig.goaMembersGroupId;

    try {
      const token = await getAccessToken(adminRequest.scopes);
      if (fromGroup)
        await removeUserFromGroup(token, fromGroup, userId).catch(() => {});
      if (toGroup) await addUserToGroup(token, toGroup, userId);
      showMessage(`Role changed to ${targetRole}`);
      await refreshUsers();
    } catch (err) {
      showMessage('Failed to change role: ' + err.message, 'error');
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      (u.email || '').toLowerCase().includes(filter.toLowerCase()) ||
      (u.displayName || '').toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="admin-panel">
      <div className="admin-container">
        <div className="admin-header">
          <h1>👤 User Management</h1>
          <p className="admin-subtitle">
            Manage platform users, roles, and credentials
          </p>
        </div>

        {message && (
          <div className={`admin-message admin-message-${message.type}`}>
            {message.type === 'success'
              ? '✅'
              : message.type === 'warning'
              ? '⚠️'
              : '❌'}{' '}
            {message.text}
          </div>
        )}

        {/* Add User Form */}
        <div className="admin-card">
          <h2>Add New User</h2>
          <form onSubmit={handleAddUser} className="add-user-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="newEmail">Email Address *</label>
                <input
                  id="newEmail"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="newName">Display Name *</label>
                <input
                  id="newName"
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="John Doe"
                  required
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="newRole">Role</label>
                <select
                  id="newRole"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="newPassword">
                  Password (leave blank to auto-generate)
                </label>
                <input
                  id="newPassword"
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Auto-generated if blank"
                  autoComplete="off"
                />
              </div>
              <div className="form-group form-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                  />
                  Send welcome email (may not work in CIAM tenants)
                </label>
              </div>
            </div>
            <button type="submit" className="btn btn-add" disabled={addingUser}>
              {addingUser ? 'Creating...' : '+ Add User'}
            </button>
          </form>

          {/* Credentials display after user creation */}
          {createdCredentials && (
            <div className="credentials-box">
              <div className="credentials-header">
                <h3>📋 New User Credentials</h3>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCreatedCredentials(null)}
                >
                  Dismiss
                </button>
              </div>
              <p className="credentials-note">
                Share these credentials securely with <strong>{createdCredentials.name}</strong>:
              </p>
              <div className="credentials-details">
                <div className="credential-row">
                  <span className="credential-label">Email:</span>
                  <code className="credential-value">{createdCredentials.email}</code>
                </div>
                <div className="credential-row">
                  <span className="credential-label">Password:</span>
                  <code className="credential-value credential-password">{createdCredentials.password}</code>
                </div>
                <div className="credential-row">
                  <span className="credential-label">Login URL:</span>
                  <code className="credential-value">{window.location.origin}</code>
                </div>
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  navigator.clipboard.writeText(
                    `Email: ${createdCredentials.email}\nPassword: ${createdCredentials.password}\nLogin: ${window.location.origin}`
                  );
                  showMessage('Credentials copied to clipboard');
                }}
              >
                📋 Copy All
              </button>
            </div>
          )}
        </div>

        {/* User List */}
        <div className="admin-card">
          <div className="user-list-header">
            <h2>Platform Users ({users.length})</h2>
            <input
              type="text"
              className="filter-input"
              placeholder="Filter users..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>

          {loadingUsers ? (
            <div className="admin-loading">
              <div className="spinner"></div>
              <p>Loading users from directory...</p>
            </div>
          ) : (
            <div className="user-table-wrapper">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr
                      key={u.id}
                      className={
                        u.id === currentUser?.id ? 'current-user-row' : ''
                      }
                    >
                      <td>
                        <span className="user-name-cell">{u.displayName}</span>
                        {u.id === currentUser?.id && (
                          <span className="badge badge-you">you</span>
                        )}
                      </td>
                      <td className="user-email">{u.email}</td>
                      <td>
                        <button
                          className={`role-badge role-${u.role}`}
                          onClick={() => handleChangeRole(u.id, u.role)}
                          disabled={u.id === currentUser?.id}
                          title={
                            u.id === currentUser?.id
                              ? 'Cannot change own role'
                              : `Click to switch to ${
                                  u.role === 'admin' ? 'member' : 'admin'
                                }`
                          }
                        >
                          {u.role}
                        </button>
                      </td>
                      <td>
                        <span
                          className={`status-badge ${
                            u.accountEnabled
                              ? 'status-active'
                              : 'status-disabled'
                          }`}
                        >
                          {u.accountEnabled ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button
                          className="btn-action btn-reset"
                          onClick={() => {
                            setResetTarget(u);
                            setResetPw(generateTempPassword());
                          }}
                          title="Reset password"
                        >
                          🔑
                        </button>
                        <button
                          className="btn-action btn-delete"
                          onClick={() => handleDeleteUser(u.id, u.email)}
                          disabled={u.id === currentUser?.id}
                          title={
                            u.id === currentUser?.id
                              ? 'Cannot delete yourself'
                              : 'Delete user'
                          }
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan="5" className="empty-row">
                        {filter
                          ? 'No users match the filter'
                          : 'No users found. Add users above to get started.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="admin-info">
          <h3>ℹ️ Access Control Info</h3>
          <ul>
            <li>
              <strong>GoaAdmin</strong> — Can view briefings and manage users
            </li>
            <li>
              <strong>GoaMembers</strong> — Can view briefings and manage their profile
            </li>
            <li>Users not in either group cannot access the platform</li>
            <li>Click a role badge to toggle between admin/member</li>
          </ul>
        </div>

        {/* Reset Password Modal */}
        {resetTarget && (
          <div className="modal-overlay" onClick={() => setResetTarget(null)}>
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Reset Password</h3>
              <p>
                Set new password for{' '}
                <strong>{resetTarget.displayName}</strong> ({resetTarget.email})
              </p>
              <form onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label htmlFor="resetPw">New Password</label>
                  <input
                    id="resetPw"
                    type="text"
                    value={resetPw}
                    onChange={(e) => setResetPw(e.target.value)}
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">
                    Reset Password
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setResetTarget(null)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;

